import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { gsap } from 'gsap';
import { createCityViewportPortal } from './city-portal.js';

const seeded = (x, z, salt = 0) => {
    const n = Math.sin(x * 127.1 + z * 311.7 + salt * 74.7) * 43758.5453123;
    return n - Math.floor(n);
};

const CITY = {
    blockSize: 38,
    roadWidth: 5.8,
    tileRadius: 3,
    projectHeight: 16,
    projectSiteSize: 17,
    minDistance: 108,
    maxDistance: 150,
    laneOffset: 1.45,
    cloudRadius: 4,
};

const CITY_PALETTES = {
    day: {
        background: 0xdde5e3,
        fog: 0xdde5e3,
        ground: 0xd4d5ce,
        block: 0xe7e4dc,
        lawn: 0xc7d2c0,
        road: 0xa7a9a6,
        roadLine: 0xf0eee7,
        building: 0xe9e7e1,
        buildingAlt: 0xd7d5cf,
        buildingDark: 0xc5c5bf,
        glass: 0xbecfd3,
        glassEdge: 0x8ea6ab,
        window: 0xb3bebd,
        tree: 0x74856f,
        trunk: 0x7c6753,
        lampPost: 0x646966,
        lamp: 0xffefbe,
        car: 0x747876,
        carAlt: 0xd0cdc4,
        carAccent: 0x98a59a,
        parkAccent: 0x93b082,
        cloud: 0xf7fbfc,
        cloudShadow: 0xd8e2e2,
        project: 0x519003,
        projectSelected: 0x82d021,
        projectSite: 0x519003,
        ambient: 0xffffff,
        sun: 0xfff5dc,
    },
    night: {
        background: 0x223039,
        fog: 0x223039,
        ground: 0x344047,
        block: 0x3a464d,
        lawn: 0x314b3b,
        road: 0x273238,
        roadLine: 0x90a0a7,
        building: 0x5a676d,
        buildingAlt: 0x4e5a61,
        buildingDark: 0x404b50,
        glass: 0x2f5b67,
        glassEdge: 0x88bac1,
        window: 0xffdba0,
        tree: 0x4a6b58,
        trunk: 0x5e4a3c,
        lampPost: 0x9aaab1,
        lamp: 0xffdd9c,
        car: 0xb0bec2,
        carAlt: 0x73848c,
        carAccent: 0xd9e1e3,
        parkAccent: 0x5b8167,
        cloud: 0x526d78,
        cloudShadow: 0x334751,
        project: 0x5aa408,
        projectSelected: 0x9cea37,
        projectSite: 0x66b80d,
        ambient: 0xa3b6c2,
        sun: 0xb8d5ec,
    },
};

const parseConfig = (root) => {
    const node = root.querySelector('[data-city-config]');
    if (!node) return { projects: [] };

    try {
        const parsed = JSON.parse(node.textContent || '{}');
        return parsed && typeof parsed === 'object' ? parsed : { projects: [] };
    } catch (error) {
        return { projects: [] };
    }
};

const clampInteger = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const damp = (current, target, lambda, delta) => THREE.MathUtils.damp(current, target, lambda, delta);
const easeInOut = (value) => {
    const t = THREE.MathUtils.clamp(value, 0, 1);
    return t * t * (3 - 2 * t);
};
const positiveModulo = (value, divisor) => ((value % divisor) + divisor) % divisor;

export function initCityExplorer(root, { lenis = null } = {}) {
    if (!root || root.dataset.cityReady === '1') return null;

    const mount = root.querySelector('[data-city-canvas]');
    if (!mount) return null;

    root.dataset.cityReady = '1';

    const config = parseConfig(root);
    const projectEntries = Array.isArray(config.projects) ? config.projects : [];
    const reservedBlocks = new Set(
        projectEntries.map((project) => `${clampInteger(project.blockX)}:${clampInteger(project.blockZ)}`)
    );

    const scene = new THREE.Scene();
    let themeMode = document.documentElement.dataset.theme === 'night' ? 'night' : 'day';
    let palette = CITY_PALETTES[themeMode];
    scene.background = new THREE.Color(palette.background);

    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 1200);
    camera.position.set(84, 96, 84);

    const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.65));
    renderer.shadowMap.enabled = window.matchMedia('(min-width: 48rem)').matches;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 2.5, 0);
    controls.enableDamping = true;
    controls.dampingFactor = 0.075;
    controls.enableRotate = true;
    controls.enablePan = true;
    controls.screenSpacePanning = false;
    controls.minDistance = CITY.minDistance;
    controls.maxDistance = CITY.maxDistance;
    controls.minPolarAngle = 0.67;
    controls.maxPolarAngle = 1.02;
    controls.panSpeed = 1.05;
    controls.rotateSpeed = 0.32;
    controls.zoomSpeed = 0.45;
    controls.mouseButtons.LEFT = THREE.MOUSE.PAN;
    controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
    controls.mouseButtons.RIGHT = THREE.MOUSE.ROTATE;
    controls.touches.ONE = THREE.TOUCH.PAN;
    controls.touches.TWO = THREE.TOUCH.DOLLY_ROTATE;
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.24;
    controls.enabled = false;
    controls.update();

    const disposableMaterials = new Set();
    const mat = (color, options = {}) => {
        const material = new THREE.MeshStandardMaterial({
            color,
            roughness: 0.9,
            metalness: 0,
            flatShading: true,
            ...options,
        });
        disposableMaterials.add(material);
        return material;
    };

    const glassMat = (color, options = {}) => {
        const material = new THREE.MeshStandardMaterial({
            color,
            roughness: 0.28,
            metalness: 0.08,
            flatShading: true,
            transparent: true,
            opacity: 0.88,
            ...options,
        });
        disposableMaterials.add(material);
        return material;
    };

    const groundMaterial = mat(palette.ground);
    const blockMaterial = mat(palette.block);
    const lawnMaterial = mat(palette.lawn);
    const roadMaterial = mat(palette.road);
    const roadLineMaterial = mat(palette.roadLine, {
        emissive: palette.roadLine,
        emissiveIntensity: themeMode === 'night' ? 0.22 : 0,
    });
    const buildingMaterial = mat(palette.building);
    const buildingAltMaterial = mat(palette.buildingAlt);
    const buildingDarkMaterial = mat(palette.buildingDark);
    const glassMaterial = glassMat(palette.glass, {
        emissive: palette.glassEdge,
        emissiveIntensity: themeMode === 'night' ? 0.18 : 0.03,
    });
    const windowMaterial = mat(palette.window, {
        emissive: palette.window,
        emissiveIntensity: themeMode === 'night' ? 1.32 : 0,
        roughness: 0.6,
    });
    const windowDarkMaterial = mat(palette.window, {
        emissive: palette.window,
        emissiveIntensity: themeMode === 'night' ? 0.025 : 0,
        roughness: 0.68,
    });
    const treeMaterial = mat(palette.tree);
    const trunkMaterial = mat(palette.trunk);
    const lampPostMaterial = mat(palette.lampPost, { roughness: 0.82 });
    const lampMaterial = mat(palette.lamp, {
        emissive: palette.lamp,
        emissiveIntensity: themeMode === 'night' ? 3.2 : 0.18,
        roughness: 0.4,
    });
    const carMaterial = mat(palette.car, { roughness: 0.8 });
    const carAltMaterial = mat(palette.carAlt, { roughness: 0.8 });
    const carAccentMaterial = mat(palette.carAccent, { roughness: 0.65 });
    const parkAccentMaterial = mat(palette.parkAccent, { roughness: 0.86 });

    const projectMaterial = mat(palette.project, {
        roughness: 0.78,
        metalness: 0.02,
        emissive: palette.project,
        emissiveIntensity: themeMode === 'night' ? 0.2 : 0,
    });
    const projectSelectedMaterial = projectMaterial.clone();
    projectSelectedMaterial.color.setHex(palette.projectSelected);
    projectSelectedMaterial.emissive.setHex(palette.projectSelected);
    projectSelectedMaterial.emissiveIntensity = themeMode === 'night' ? 0.58 : 0.08;
    disposableMaterials.add(projectSelectedMaterial);

    const projectSiteMaterial = mat(palette.projectSite, {
        roughness: 0.92,
        transparent: true,
        opacity: 0.16,
        emissive: palette.projectSite,
        emissiveIntensity: themeMode === 'night' ? 0.25 : 0.04,
        depthWrite: false,
    });
    const projectLoadingMaterial = new THREE.MeshBasicMaterial({
        color: palette.projectSite,
        wireframe: true,
        transparent: true,
        opacity: 0.4,
    });
    disposableMaterials.add(projectLoadingMaterial);

    const hemisphere = new THREE.HemisphereLight(
        palette.ambient,
        palette.ground,
        themeMode === 'night' ? 1.65 : 2.45
    );
    scene.add(hemisphere);

    const sun = new THREE.DirectionalLight(palette.sun, themeMode === 'night' ? 2.1 : 3.05);
    sun.position.set(-58, 94, 46);
    sun.castShadow = renderer.shadowMap.enabled;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -110;
    sun.shadow.camera.right = 110;
    sun.shadow.camera.top = 110;
    sun.shadow.camera.bottom = -110;
    scene.add(sun);
    scene.add(sun.target);

    const world = new THREE.Group();
    world.name = 'Infinite procedural city';
    scene.add(world);

    const projectLayer = new THREE.Group();
    projectLayer.name = 'LED Projects';
    scene.add(projectLayer);

    const trafficLayer = new THREE.Group();
    trafficLayer.name = 'Low-poly traffic';
    scene.add(trafficLayer);

    const nightLightPool = [];
    for (let i = 0; i < 9; i += 1) {
        const light = new THREE.PointLight(palette.lamp, themeMode === 'night' ? 26 : 0, 22, 2);
        light.position.y = 4.8;
        scene.add(light);
        nightLightPool.push(light);
    }

    const finalizeMesh = (mesh, receiveShadow = true) => {
        mesh.castShadow = renderer.shadowMap.enabled;
        mesh.receiveShadow = receiveShadow;
        return mesh;
    };

    const addWindowGrid = (building, width, depth, height, options = {}) => {
        const {
            frontRows = 4,
            sideRows = 4,
            glass = false,
            floors = null,
        } = options;
        if (height < 2.8) return;

        const floorCount = Math.max(2, floors || Math.min(8, Math.floor(height / 1.45)));
        const usableHeight = Math.max(0.8, height - 1.2);
        const floorSpacing = usableHeight / floorCount;
        const frontCols = Math.max(2, Math.floor((width - 1.2) / (glass ? 1.8 : 1.3)));
        const sideCols = Math.max(2, Math.floor((depth - 1.2) / (glass ? 1.9 : 1.35)));
        const frontWindowW = Math.min(glass ? 1.15 : 0.7, (width - 1.4) / frontCols * 0.8);
        const sideWindowW = Math.min(glass ? 1.15 : 0.7, (depth - 1.4) / sideCols * 0.8);
        const windowH = Math.min(glass ? 0.72 : 0.48, floorSpacing * 0.56);
        const buildingSeed = Math.abs(Math.round((width * 13) + (depth * 23) + (height * 37)));
        const buildingLightsOn = buildingSeed % 5 !== 0;
        const getWindowMaterial = (index) => {
            if (glass) return glassMaterial;
            if (!buildingLightsOn) return windowDarkMaterial;
            return ((buildingSeed + index * 11) % 7 === 0) ? windowDarkMaterial : windowMaterial;
        };

        for (let row = 0; row < floorCount; row += 1) {
            const y = -height / 2 + 0.82 + row * floorSpacing;
            for (let col = 0; col < frontCols; col += 1) {
                const x = -width / 2 + 0.9 + col * ((width - 1.8) / Math.max(1, frontCols - 1));
                const front = new THREE.Mesh(
                    new THREE.BoxGeometry(frontWindowW, windowH, 0.06),
                    getWindowMaterial(row * frontCols + col)
                );
                front.position.set(x, y, depth / 2 + 0.04);
                building.add(front);

                if (seeded(width + row, depth + col, 811) > 0.08) {
                    const back = front.clone();
                    back.position.z = -depth / 2 - 0.04;
                    building.add(back);
                }
            }

            for (let col = 0; col < sideCols; col += 1) {
                const z = -depth / 2 + 0.9 + col * ((depth - 1.8) / Math.max(1, sideCols - 1));
                const side = new THREE.Mesh(
                    new THREE.BoxGeometry(0.06, windowH, sideWindowW),
                    getWindowMaterial((row * sideCols + col) + 101)
                );
                side.position.set(width / 2 + 0.04, y, z);
                building.add(side);

                if (seeded(width + row, depth + col, 913) > 0.14) {
                    const sideBack = side.clone();
                    sideBack.position.x = -width / 2 - 0.04;
                    building.add(sideBack);
                }
            }
        }

        if (frontRows > 0 && glass) {
            const crownBand = new THREE.Mesh(
                new THREE.BoxGeometry(width * 0.88, Math.min(0.45, height * 0.05), depth * 0.9),
                glassMaterial
            );
            crownBand.position.y = height / 2 - 0.4;
            building.add(crownBand);
        }
    };

    const addHelipad = (group, size = 4.4) => {
        const padMesh = finalizeMesh(new THREE.Mesh(new THREE.CylinderGeometry(size, size, 0.06, 8), buildingDarkMaterial), false);
        padMesh.position.y = 0.03;
        group.add(padMesh);

        const hLeft = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.05, size * 0.72), roadLineMaterial);
        const hRight = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.05, size * 0.72), roadLineMaterial);
        const hMiddle = new THREE.Mesh(new THREE.BoxGeometry(size * 0.52, 0.05, 0.3), roadLineMaterial);
        hLeft.position.set(-size * 0.22, 0.06, 0);
        hRight.position.set(size * 0.22, 0.06, 0);
        hMiddle.position.y = 0.06;
        group.add(hLeft, hRight, hMiddle);
    };

    const makeRoundedTree = (variant = 0) => {
        const group = new THREE.Group();
        const trunk = finalizeMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.22, 1, 6), trunkMaterial));
        trunk.position.y = 0.5;
        group.add(trunk);

        if (variant % 3 === 0) {
            const crown = finalizeMesh(new THREE.Mesh(new THREE.IcosahedronGeometry(0.9, 0), treeMaterial), false);
            crown.position.y = 1.55;
            crown.scale.set(1, 0.92, 1);
            group.add(crown);
        } else if (variant % 3 === 1) {
            const lower = finalizeMesh(new THREE.Mesh(new THREE.IcosahedronGeometry(0.72, 0), treeMaterial), false);
            const upper = finalizeMesh(new THREE.Mesh(new THREE.IcosahedronGeometry(0.58, 0), treeMaterial), false);
            lower.position.y = 1.3;
            upper.position.y = 1.98;
            group.add(lower, upper);
        } else {
            const crown = finalizeMesh(new THREE.Mesh(new THREE.SphereGeometry(0.85, 8, 6), treeMaterial), false);
            crown.position.y = 1.58;
            crown.scale.set(1.08, 0.82, 1.08);
            group.add(crown);
        }

        return group;
    };

    const makeLamp = () => {
        const group = new THREE.Group();
        const post = finalizeMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.07, 3.4, 6), lampPostMaterial), false);
        post.position.y = 1.7;
        group.add(post);

        const arm = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.08, 0.08), lampPostMaterial), false);
        arm.position.set(0.26, 3.24, 0);
        group.add(arm);

        const head = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.18, 0.34), lampMaterial), false);
        head.position.set(0.52, 3.08, 0);
        group.add(head);
        return group;
    };

    const addDashedRoadMarkings = (group, tx, tz) => {
        const roadLength = CITY.blockSize + CITY.roadWidth;
        const segmentLength = 3.2;
        const segmentGap = 2.3;
        const segmentCount = Math.floor(roadLength / (segmentLength + segmentGap));

        for (let i = 0; i < segmentCount; i += 1) {
            const progress = i - (segmentCount - 1) / 2;
            const offset = progress * (segmentLength + segmentGap);

            const horizontalSegment = new THREE.Mesh(new THREE.BoxGeometry(segmentLength, 0.012, 0.07), roadLineMaterial);
            horizontalSegment.position.set(CITY.roadWidth / 2 + offset, 0.065, CITY.blockSize / 2);
            group.add(horizontalSegment);

            const verticalSegment = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.012, segmentLength), roadLineMaterial);
            verticalSegment.position.set(CITY.blockSize / 2, 0.068, CITY.roadWidth / 2 + offset);
            group.add(verticalSegment);
        }
    };

    const addTreesCluster = (group, positions, seedX, seedZ) => {
        positions.forEach(([x, z], index) => {
            if (seeded(seedX, seedZ, 320 + index) < 0.12) return;
            const tree = makeRoundedTree(index + seedX + seedZ);
            tree.position.set(x, 0.05, z);
            tree.rotation.y = seeded(seedX, seedZ, 340 + index) * Math.PI;
            tree.scale.setScalar(0.86 + seeded(seedX, seedZ, 360 + index) * 0.35);
            group.add(tree);
        });
    };

    const addStripWindows = (building, width, depth, height, options = {}) => {
        const {
            orientation = 'vertical',
            glass = true,
            bands = 4,
            lights = 'auto',
        } = options;

        const bandCount = Math.max(2, bands);
        const sideBands = Math.max(2, Math.round(bandCount * 0.75));
        const buildingSeed = Math.abs(Math.round((width * 17) + (depth * 29) + (height * 41)));
        const buildingLightsOn = lights === true || (lights === 'auto' && (buildingSeed % 4 !== 0));
        const darkMaterial = glass ? glassMaterial : windowDarkMaterial;
        const getBandMaterial = (index) => {
            if (!buildingLightsOn) return darkMaterial;
            // Keep some bands dark so Night mode does not look artificially uniform.
            return ((buildingSeed + index * 7) % 5 === 0) ? darkMaterial : windowMaterial;
        };

        if (orientation === 'vertical') {
            const stripHeight = Math.max(1.8, height * 0.76);
            const frontStripWidth = Math.min(0.95, Math.max(0.38, width * 0.075));
            const sideStripWidth = Math.min(0.85, Math.max(0.34, depth * 0.075));

            for (let i = 0; i < bandCount; i += 1) {
                const progress = bandCount === 1 ? 0.5 : i / (bandCount - 1);
                const x = -width / 2 + 1.15 + progress * Math.max(0.1, width - 2.3);
                const material = getBandMaterial(i);
                const front = new THREE.Mesh(new THREE.BoxGeometry(frontStripWidth, stripHeight, 0.055), material);
                // building is already centered on its own local origin, so window geometry must be centered at y=0.
                front.position.set(x, 0, depth / 2 + 0.032);
                building.add(front);

                const back = front.clone();
                back.position.z = -depth / 2 - 0.032;
                building.add(back);
            }

            for (let i = 0; i < sideBands; i += 1) {
                const progress = sideBands === 1 ? 0.5 : i / (sideBands - 1);
                const z = -depth / 2 + 1.05 + progress * Math.max(0.1, depth - 2.1);
                const material = getBandMaterial(i + bandCount);
                const side = new THREE.Mesh(new THREE.BoxGeometry(0.055, stripHeight, sideStripWidth), material);
                side.position.set(width / 2 + 0.032, 0, z);
                building.add(side);

                const sideBack = side.clone();
                sideBack.position.x = -width / 2 - 0.032;
                building.add(sideBack);
            }
            return;
        }

        const stripHeight = Math.min(0.46, Math.max(0.24, height * 0.052));
        const usableHeight = Math.max(0.4, height - 2.0);
        for (let i = 0; i < bandCount; i += 1) {
            const progress = bandCount === 1 ? 0.5 : i / (bandCount - 1);
            // Local Y must run from -height/2 to +height/2.
            const y = -height / 2 + 1.0 + progress * usableHeight;
            const material = getBandMaterial(i);

            const front = new THREE.Mesh(new THREE.BoxGeometry(width * 0.82, stripHeight, 0.055), material);
            front.position.set(0, y, depth / 2 + 0.032);
            building.add(front);

            const back = front.clone();
            back.position.z = -depth / 2 - 0.032;
            building.add(back);

            const side = new THREE.Mesh(new THREE.BoxGeometry(0.055, stripHeight, depth * 0.76), material);
            side.position.set(width / 2 + 0.032, y, 0);
            building.add(side);

            const sideBack = side.clone();
            sideBack.position.x = -width / 2 - 0.032;
            building.add(sideBack);
        }
    };

    const createHouse = (options = {}) => {
        const {
            width = 8.2,
            depth = 6.8,
            height = 3.6,
            material = buildingMaterial,
        } = options;

        const group = new THREE.Group();
        const body = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material));
        body.position.y = height / 2;
        group.add(body);

        const roof = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(width * 1.04, 0.32, depth * 1.04), buildingDarkMaterial));
        roof.position.y = height + 0.16;
        group.add(roof);

        addWindowGrid(body, width, depth, height, { floors: 2, glass: false });

        const door = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.8, 0.08), buildingDarkMaterial);
        door.position.set(0, 0.9, depth / 2 + 0.05);
        group.add(door);
        return group;
    };

    const createGlassTower = (options = {}) => {
        const {
            width = 8,
            depth = 8,
            height = 11,
            podium = true,
            helipad = false,
        } = options;
        const group = new THREE.Group();

        if (podium) {
            const base = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(width + 3.6, 1.5, depth + 3.6), buildingAltMaterial));
            base.position.y = 0.75;
            group.add(base);
        }

        const body = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), buildingMaterial));
        body.position.y = (podium ? 1.5 : 0) + height / 2;
        group.add(body);
        const stripMode = ((Math.round(width + depth + height) % 3) !== 0);
        if (stripMode) addStripWindows(body, width, depth, height, { glass: true, orientation: (Math.round(width + height) % 2 === 0 ? 'vertical' : 'horizontal'), bands: Math.max(3, Math.floor(width / 2.4)) });
        else addWindowGrid(body, width, depth, height, { glass: true });

        if (helipad) {
            const roofGroup = new THREE.Group();
            roofGroup.position.set(0, body.position.y + height / 2 + 0.06, 0);
            addHelipad(roofGroup, Math.min(width, depth) * 0.38);
            group.add(roofGroup);
        }

        return group;
    };

    const createMidrise = (options = {}) => {
        const {
            width = 11,
            depth = 8,
            height = 7,
            glass = false,
            material = buildingAltMaterial,
        } = options;
        const group = new THREE.Group();
        const body = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material));
        body.position.y = height / 2;
        group.add(body);
        const useStrips = (Math.round(width + depth) % 2 === 0);
        if (useStrips) addStripWindows(body, width, depth, height, { glass, orientation: (Math.round(width) % 2 === 0 ? 'horizontal' : 'vertical'), bands: Math.max(3, Math.floor(height / 2)) });
        else addWindowGrid(body, width, depth, height, { glass, floors: Math.max(3, Math.floor(height / 1.5)) });

        const crown = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(width * 0.92, 0.42, depth * 0.92), glass ? glassMaterial : buildingDarkMaterial));
        crown.position.y = height + 0.21;
        group.add(crown);
        return group;
    };

    const createMall = () => {
        const group = new THREE.Group();
        const podium = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(17, 3.2, 12.2), buildingAltMaterial));
        podium.position.y = 1.6;
        group.add(podium);
        addWindowGrid(podium, 17, 12.2, 3.2, { floors: 2, glass: true });

        const glassFront = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(12.8, 1.7, 0.16), glassMaterial), false);
        glassFront.position.set(0, 1.35, 6.2);
        group.add(glassFront);

        const entranceCanopy = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(5.2, 0.24, 1.5), buildingDarkMaterial), false);
        entranceCanopy.position.set(0, 2.35, 6.9);
        group.add(entranceCanopy);
        return group;
    };

    const createFactory = () => {
        const group = new THREE.Group();
        const shed = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(15.8, 4.3, 10.8), buildingDarkMaterial));
        shed.position.y = 2.15;
        group.add(shed);
        const roof = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(16.3, 0.36, 11.2), buildingAltMaterial));
        roof.position.y = 4.62;
        group.add(roof);

        addWindowGrid(shed, 15.8, 10.8, 4.3, { floors: 2, glass: false });

        const chimney = finalizeMesh(new THREE.Mesh(new THREE.CylinderGeometry(0.65, 0.8, 7.6, 8), buildingAltMaterial));
        chimney.position.set(5.3, 3.8, -3.2);
        group.add(chimney);
        return group;
    };

    const createGasStation = () => {
        const group = new THREE.Group();
        const shop = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(7.8, 3.1, 5.8), buildingMaterial));
        shop.position.set(-4.2, 1.55, 0);
        group.add(shop);
        addWindowGrid(shop, 7.8, 5.8, 3.1, { floors: 2, glass: true });

        const canopy = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(10.5, 0.3, 6.5), buildingAltMaterial));
        canopy.position.set(3.6, 3.05, 0);
        group.add(canopy);

        [-1.4, 1.4].forEach((z) => {
            const pump = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(0.48, 1.12, 0.48), carAccentMaterial));
            pump.position.set(3.2, 0.56, z);
            group.add(pump);
        });
        return group;
    };

    const createPlayground = () => {
        const group = new THREE.Group();
        const court = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(14, 0.08, 10), parkAccentMaterial), false);
        court.position.y = 0.06;
        group.add(court);

        const lineA = new THREE.Mesh(new THREE.BoxGeometry(12.4, 0.02, 0.14), roadLineMaterial);
        lineA.position.y = 0.11;
        const lineB = lineA.clone();
        lineA.position.z = -4.4;
        lineB.position.z = 4.4;
        const lineC = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.02, 8.9), roadLineMaterial);
        lineC.position.y = 0.11;
        lineC.position.x = -5.8;
        const lineD = lineC.clone();
        lineD.position.x = 5.8;
        group.add(lineA, lineB, lineC, lineD);
        return group;
    };

    const addParkBlock = (group, tx, tz) => {
        const lawn = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(26.5, 0.1, 26.5), lawnMaterial));
        lawn.position.set(0, 0.03, 0);
        group.add(lawn);

        if (seeded(tx, tz, 514) > 0.52) {
            group.add(createPlayground());
        }

        addTreesCluster(group, [
            [-8.8, -8.2], [8.6, -8.4], [-8.1, 8.1], [8.5, 8.7],
            [-11.5, 0], [11.5, 0], [0, -11.3], [0, 11.2],
        ], tx, tz);
    };

    const createSimpleMass = ({ width = 10, depth = 9, height = 7, material = buildingMaterial } = {}) => {
        const group = new THREE.Group();
        const body = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material));
        body.position.y = height / 2;
        group.add(body);

        const cap = finalizeMesh(new THREE.Mesh(
            new THREE.BoxGeometry(width * 0.94, 0.18, depth * 0.94),
            material === buildingDarkMaterial ? buildingAltMaterial : buildingDarkMaterial,
        ));
        cap.position.y = height + 0.09;
        group.add(cap);
        return group;
    };

    const addCivicBlock = (group, tx, tz) => {
        const r = seeded(tx, tz, 480);
        const vary = (base, salt, range = 1.6) => base + (seeded(tx, tz, salt) - 0.5) * range;

        if (r < 0.14) {
            addParkBlock(group, tx, tz);
            return;
        }

        if (r < 0.46) {
            const mass = createSimpleMass({
                width: vary(12.5, 511, 3.4),
                depth: vary(10.5, 521, 2.8),
                height: vary(6.8, 531, 4.2),
                material: r < 0.30 ? buildingMaterial : buildingAltMaterial,
            });
            group.add(mass);
            return;
        }

        if (r < 0.78) {
            const massA = createSimpleMass({
                width: vary(9.2, 551, 2.2),
                depth: vary(8.4, 561, 2.0),
                height: vary(8.5, 571, 4.8),
                material: buildingMaterial,
            });
            const massB = createSimpleMass({
                width: vary(7.4, 581, 1.8),
                depth: vary(7.0, 591, 1.6),
                height: vary(5.6, 601, 2.8),
                material: buildingAltMaterial,
            });
            massA.position.x = -5.4;
            massB.position.x = 6.7;
            group.add(massA, massB);
            return;
        }

        const tower = createSimpleMass({
            width: vary(9.2, 621, 1.8),
            depth: vary(9.0, 631, 1.8),
            height: vary(13.2, 641, 5.0),
            material: r < 0.9 ? buildingDarkMaterial : buildingAltMaterial,
        });
        group.add(tower);
    };


    const activeTiles = new Map();
    let currentTileX = Number.NaN;
    let currentTileZ = Number.NaN;

    const createTile = (tx, tz) => {
        const group = new THREE.Group();
        group.position.set(tx * CITY.blockSize, 0, tz * CITY.blockSize);
        group.userData.tile = { tx, tz };

        const base = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(CITY.blockSize, 0.16, CITY.blockSize), groundMaterial));
        base.position.y = -0.12;
        group.add(base);

        const innerSize = CITY.blockSize - CITY.roadWidth - 1.2;
        const block = finalizeMesh(new THREE.Mesh(new THREE.BoxGeometry(innerSize, 0.1, innerSize), blockMaterial));
        block.position.set(0, -0.015, 0);
        group.add(block);

        const roadHorizontal = finalizeMesh(new THREE.Mesh(
            new THREE.BoxGeometry(CITY.blockSize + CITY.roadWidth, 0.075, CITY.roadWidth),
            roadMaterial
        ));
        roadHorizontal.position.set(CITY.roadWidth / 2, 0.02, CITY.blockSize / 2);
        group.add(roadHorizontal);

        const roadVertical = finalizeMesh(new THREE.Mesh(
            new THREE.BoxGeometry(CITY.roadWidth, 0.08, CITY.blockSize + CITY.roadWidth),
            roadMaterial
        ));
        roadVertical.position.set(CITY.blockSize / 2, 0.025, CITY.roadWidth / 2);
        group.add(roadVertical);

        addDashedRoadMarkings(group, tx, tz);

        const isProjectBlock = reservedBlocks.has(`${tx}:${tz}`);
        const canLandscape = !isProjectBlock;
        if (canLandscape) addCivicBlock(group, tx, tz);

        const cornerOffset = innerSize / 2 - 1.25;
        const lampPositions = [
            [-cornerOffset, -cornerOffset],
            [cornerOffset, -cornerOffset],
            [-cornerOffset, cornerOffset],
            [cornerOffset, cornerOffset],
        ];
        lampPositions.forEach(([x, z], index) => {
            if (seeded(tx, tz, 400 + index) < 0.1) return;
            const lamp = makeLamp();
            lamp.position.set(x, 0.08, z);
            lamp.rotation.y = seeded(tx, tz, 430 + index) * Math.PI * 2;
            group.add(lamp);
        });

        return group;
    };

    const tileKey = (x, z) => `${x}:${z}`;

    const refreshNightLights = () => {
        const baseX = Math.round((controls.target.x - CITY.blockSize / 2) / CITY.blockSize);
        const baseZ = Math.round((controls.target.z - CITY.blockSize / 2) / CITY.blockSize);
        let index = 0;

        sun.position.set(controls.target.x - 58, 94, controls.target.z + 46);
        sun.target.position.set(controls.target.x, 0, controls.target.z);
        sun.target.updateMatrixWorld();

        for (let x = -1; x <= 1; x += 1) {
            for (let z = -1; z <= 1; z += 1) {
                const light = nightLightPool[index];
                light.position.set(
                    (baseX + x + 0.5) * CITY.blockSize,
                    4.6,
                    (baseZ + z + 0.5) * CITY.blockSize
                );
                index += 1;
            }
        }
    };

    const refreshTiles = () => {
        const tx = Math.round(controls.target.x / CITY.blockSize);
        const tz = Math.round(controls.target.z / CITY.blockSize);
        if (tx === currentTileX && tz === currentTileZ) return;

        currentTileX = tx;
        currentTileZ = tz;

        const needed = new Set();
        for (let x = tx - CITY.tileRadius; x <= tx + CITY.tileRadius; x += 1) {
            for (let z = tz - CITY.tileRadius; z <= tz + CITY.tileRadius; z += 1) {
                const key = tileKey(x, z);
                needed.add(key);
                if (!activeTiles.has(key)) {
                    const tile = createTile(x, z);
                    activeTiles.set(key, tile);
                    world.add(tile);
                }
            }
        }

        [...activeTiles.entries()].forEach(([key, tile]) => {
            if (needed.has(key)) return;
            world.remove(tile);
            tile.traverse((child) => {
                if (child.geometry) child.geometry.dispose();
            });
            activeTiles.delete(key);
        });

        refreshNightLights();
    };

    refreshTiles();

    const makeCar = (axis, direction, kind = 0) => {
        const group = new THREE.Group();
        let body;
        let cabin;

        if (kind === 0) {
            body = new THREE.Mesh(new THREE.BoxGeometry(2.05, 0.48, 1.0), carMaterial);
            cabin = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.42, 0.9), carAltMaterial);
            cabin.position.set(-0.12, 0.78, 0);
        } else if (kind === 1) {
            body = new THREE.Mesh(new THREE.BoxGeometry(2.35, 0.56, 1.06), carAltMaterial);
            cabin = new THREE.Mesh(new THREE.BoxGeometry(1.3, 0.46, 0.94), carAccentMaterial);
            cabin.position.set(-0.18, 0.86, 0);
        } else if (kind === 2) {
            body = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.66, 1.1), carMaterial);
            cabin = new THREE.Mesh(new THREE.BoxGeometry(1.15, 0.54, 0.95), carAccentMaterial);
            cabin.position.set(0.25, 0.98, 0);
        } else {
            body = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.44, 0.94), carAccentMaterial);
            cabin = new THREE.Mesh(new THREE.BoxGeometry(0.86, 0.36, 0.82), carAltMaterial);
            cabin.position.set(-0.06, 0.72, 0);
        }

        body.position.y = 0.38;
        cabin.position.y += 0;
        group.add(body, cabin);

        const wheelA = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.22, 0.18), buildingDarkMaterial);
        const wheelB = wheelA.clone();
        const wheelC = wheelA.clone();
        const wheelD = wheelA.clone();
        wheelA.position.set(-0.72, 0.12, 0.46);
        wheelB.position.set(0.72, 0.12, 0.46);
        wheelC.position.set(-0.72, 0.12, -0.46);
        wheelD.position.set(0.72, 0.12, -0.46);
        group.add(wheelA, wheelB, wheelC, wheelD);

        if (axis === 'z') group.rotation.y = Math.PI / 2;
        if (direction < 0) group.rotation.y += Math.PI;
        group.userData.axis = axis;
        group.userData.direction = direction;
        group.userData.halfLength = [1.03, 1.18, 1.30, 0.90][kind] || 1.1;
        return group;
    };

    const vehicles = [];
    const roadOffsets = [-2, -1, 0, 1, 2];
    roadOffsets.forEach((roadOffset, roadIndex) => {
        ['x', 'z'].forEach((axis, axisIndex) => {
            [-1, 1].forEach((direction, directionIndex) => {
                const kind = (roadIndex + axisIndex * 2 + directionIndex * 3) % 4;
                const car = makeCar(axis, direction, kind);
                car.userData.roadOffset = roadOffset;
                car.userData.baseCell = roadIndex * 2 + axisIndex * 3 + directionIndex * 5;
                car.userData.lane = direction > 0 ? -CITY.laneOffset : CITY.laneOffset;
                trafficLayer.add(car);
                vehicles.push(car);
            });
        });
    });

    const updateTraffic = (elapsed) => {
        // One deterministic signal cycle: X traffic, safety pause, Z traffic, safety pause.
        // Cars move from one stop line to the next only during their green phase.
        const cycle = 11.2;
        const xGreenStart = 0.0;
        const xGreenEnd = 4.35;
        const zGreenStart = 5.6;
        const zGreenEnd = 9.95;
        const phase = positiveModulo(elapsed, cycle);
        const cycleIndex = Math.floor(elapsed / cycle);

        const phaseProgress = (start, end) => {
            if (phase <= start) return 0;
            if (phase >= end) return 1;
            return easeInOut((phase - start) / (end - start));
        };

        const xProgress = phaseProgress(xGreenStart, xGreenEnd);
        const zProgress = phaseProgress(zGreenStart, zGreenEnd);
        const centerX = Math.round(controls.target.x / CITY.blockSize) * CITY.blockSize;
        const centerZ = Math.round(controls.target.z / CITY.blockSize) * CITY.blockSize;
        const wrapSpan = CITY.blockSize * 10;

        vehicles.forEach((car) => {
            const { axis, direction, roadOffset, baseCell, lane, halfLength } = car.userData;
            const progress = axis === 'x' ? xProgress : zProgress;
            // Intersection half-width + vehicle half-length + a small safety buffer.
            const stopOffset = (CITY.roadWidth / 2) + halfLength + 0.7;
            const journey = cycleIndex + progress;
            const stopLine = (baseCell + 0.5) * CITY.blockSize - direction * stopOffset;
            const signedTravel = stopLine + direction * journey * CITY.blockSize;

            if (axis === 'x') {
                const wrappedX = centerX + positiveModulo(signedTravel - centerX + wrapSpan / 2, wrapSpan) - wrapSpan / 2;
                const roadZ = (Math.round(controls.target.z / CITY.blockSize) + roadOffset + 0.5) * CITY.blockSize;
                car.position.set(wrappedX, 0.08, roadZ + lane);
            } else {
                const wrappedZ = centerZ + positiveModulo(signedTravel - centerZ + wrapSpan / 2, wrapSpan) - wrapSpan / 2;
                const roadX = (Math.round(controls.target.x / CITY.blockSize) + roadOffset + 0.5) * CITY.blockSize;
                car.position.set(roadX - lane, 0.08, wrappedZ);
            }
        });
    };

    const interactiveRoots = [];
    const projectSitesById = new Map();
    const loader = new OBJLoader();

    const projectWorldPosition = (project) => ({
        x: clampInteger(project.blockX) * CITY.blockSize,
        z: clampInteger(project.blockZ) * CITY.blockSize,
    });

    const makeProjectSite = (project) => {
        const position = projectWorldPosition(project);
        const group = new THREE.Group();
        group.position.set(position.x, 0, position.z);
        group.userData.project = project;
        group.userData.isProjectSite = true;

        const outlineHorizontalA = new THREE.Mesh(new THREE.BoxGeometry(CITY.projectSiteSize, 0.03, 0.12), roadLineMaterial);
        const outlineHorizontalB = outlineHorizontalA.clone();
        const outlineVerticalA = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.03, CITY.projectSiteSize), roadLineMaterial);
        const outlineVerticalB = outlineVerticalA.clone();
        outlineHorizontalA.position.set(0, 0.04, -CITY.projectSiteSize / 2);
        outlineHorizontalB.position.set(0, 0.04, CITY.projectSiteSize / 2);
        outlineVerticalA.position.set(-CITY.projectSiteSize / 2, 0.04, 0);
        outlineVerticalB.position.set(CITY.projectSiteSize / 2, 0.04, 0);
        [outlineHorizontalA, outlineHorizontalB, outlineVerticalA, outlineVerticalB].forEach((line) => {
            line.userData.projectRoot = group;
            group.add(line);
        });

        const loading = new THREE.Mesh(
            new THREE.BoxGeometry(10.5, 10.5, 10.5),
            projectLoadingMaterial
        );
        loading.position.y = 5.35;
        loading.userData.projectRoot = group;
        group.add(loading);
        group.userData.loadingMesh = loading;

        projectLayer.add(group);
        interactiveRoots.push(group);
        projectSitesById.set(String(project.id), group);
        return group;
    };

    const setProjectMeshMaterial = (rootObject, active) => {
        if (!rootObject) return;
        rootObject.traverse((child) => {
            if (!child.isMesh || child === rootObject.userData?.loadingMesh) return;
            if (child.material === projectSiteMaterial || child.material === projectLoadingMaterial || child.material === roadLineMaterial) return;
            child.material = active ? projectSelectedMaterial : projectMaterial;
        });
    };

    const normalizeProject = (object, project, site) => {
        object.updateMatrixWorld(true);
        let box = new THREE.Box3().setFromObject(object);
        const size = box.getSize(new THREE.Vector3());
        const sourceHeight = Math.max(size.y, 0.001);
        const customScale = Math.max(0.05, Number(project.scale) || 1);
        const fitScale = (CITY.projectHeight / sourceHeight) * customScale;
        object.scale.setScalar(fitScale);
        object.updateMatrixWorld(true);

        box = new THREE.Box3().setFromObject(object);
        const center = box.getCenter(new THREE.Vector3());
        object.position.x -= center.x;
        object.position.z -= center.z;
        object.position.y -= box.min.y;
        object.rotation.y = THREE.MathUtils.degToRad(Number(project.rotation) || 0);
        object.userData.project = project;

        object.traverse((child) => {
            if (!child.isMesh) return;
            child.material = projectMaterial;
            child.castShadow = renderer.shadowMap.enabled;
            child.receiveShadow = true;
            child.userData.projectRoot = site;
        });

        site.add(object);
        site.userData.model = object;
        if (site.userData.loadingMesh) site.userData.loadingMesh.visible = false;
    };

    const addFallbackProject = (project, site) => {
        const group = new THREE.Group();
        const podium = new THREE.Mesh(new THREE.BoxGeometry(9.4, 1.5, 9.4), projectMaterial);
        podium.position.y = 0.75;
        group.add(podium);
        const tower = new THREE.Mesh(new THREE.BoxGeometry(6.5, 12.4, 6.5), projectMaterial);
        tower.position.y = 7.7;
        group.add(tower);
        addWindowGrid(tower, 6.5, 6.5, 12.4, { glass: true, floors: 8 });
        group.rotation.y = THREE.MathUtils.degToRad(Number(project.rotation) || 0);
        group.userData.project = project;
        group.traverse((child) => {
            if (!child.isMesh) return;
            child.castShadow = renderer.shadowMap.enabled;
            child.receiveShadow = true;
            child.userData.projectRoot = site;
        });
        site.add(group);
        site.userData.model = group;
        if (site.userData.loadingMesh) site.userData.loadingMesh.visible = false;
    };

    projectEntries.forEach((project) => {
        const site = makeProjectSite(project);
        if (!project?.modelUrl || !/\.obj(?:$|\?)/i.test(project.modelUrl)) {
            addFallbackProject(project, site);
            return;
        }

        loader.load(
            project.modelUrl,
            (object) => normalizeProject(object, project, site),
            undefined,
            () => addFallbackProject(project, site)
        );
    });

    const projectCard = root.querySelector('[data-city-project-card]');
    const projectCardMedia = root.querySelector('[data-city-project-media]');
    const projectCardImage = root.querySelector('[data-city-project-image]');
    const projectCardType = root.querySelector('[data-city-project-type]');
    const projectCardTitle = root.querySelector('[data-city-project-title]');
    const projectCardLink = root.querySelector('[data-city-project-link]');
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    const cameraMove = {
        active: false,
        duration: 1.25,
        elapsed: 0,
        startTarget: new THREE.Vector3(),
        endTarget: new THREE.Vector3(),
        startPosition: new THREE.Vector3(),
        endPosition: new THREE.Vector3(),
    };

    let selectedProject = null;
    let pointerDown = null;
    let isActive = false;
    let isTransitioning = false;
    let autoRotateTarget = 0.24;
    let autoRotateSpeed = 0.24;
    let closeTimer = 0;
    const viewportPortal = createCityViewportPortal(root);

    const showProjectCard = () => {
        if (!projectCard) return;
        projectCard.hidden = false;
        root.classList.add('is-city-project-card-open');
    };

    const hideProjectCard = () => {
        if (!projectCard) return;
        root.classList.remove('is-city-project-card-open');
        window.setTimeout(() => {
            if (!root.classList.contains('is-city-project-card-open')) {
                projectCard.hidden = true;
            }
        }, 220);
    };

    const updateProjectCard = (projectRoot) => {
        const project = projectRoot?.userData?.project;
        if (!project) {
            hideProjectCard();
            return;
        }

        if (projectCardTitle) projectCardTitle.textContent = project.title || 'Project';
        if (projectCardType) projectCardType.textContent = project.type || '';
        if (projectCardImage && projectCardMedia) {
            const imageUrl = String(project.image || '').trim();
            projectCardMedia.hidden = !imageUrl;
            if (imageUrl) {
                projectCardImage.src = imageUrl;
                projectCardImage.alt = project.title || 'Project';
            } else {
                projectCardImage.removeAttribute('src');
                projectCardImage.alt = '';
            }
        }
        if (projectCardLink) {
            projectCardLink.href = project.url || '#';
            projectCardLink.hidden = !project.url;
        }
        showProjectCard();
    };

    const findProjectRoot = (object) => {
        let current = object;
        while (current) {
            if (current.userData?.isProjectSite && current.userData?.project) return current;
            if (current.userData?.projectRoot?.userData?.project) return current.userData.projectRoot;
            current = current.parent;
        }
        return null;
    };

    const updatePointer = (event) => {
        const rect = renderer.domElement.getBoundingClientRect();
        pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };

    const projectAtPointer = (event) => {
        updatePointer(event);
        raycaster.setFromCamera(pointer, camera);
        const hit = raycaster.intersectObjects(interactiveRoots, true)[0];
        return hit ? findProjectRoot(hit.object) : null;
    };

    const hideProjectSelection = () => {
        setProjectMeshMaterial(selectedProject, false);
        selectedProject = null;
        autoRotateTarget = 0.18;
        updateProjectCard(null);
    };

    const stopAutomaticOrbit = () => {
        autoRotateTarget = 0;
    };

    const animateToProject = (projectRoot) => {
        if (!projectRoot) return;

        const target = new THREE.Vector3(projectRoot.position.x, 4.6, projectRoot.position.z);
        const dirX = projectRoot.position.x >= controls.target.x ? 1 : -1;
        const dirZ = projectRoot.position.z >= controls.target.z ? 1 : -1;
        const endPosition = new THREE.Vector3(
            target.x + dirX * 70,
            82,
            target.z + dirZ * 70
        );

        cameraMove.active = true;
        cameraMove.elapsed = 0;
        cameraMove.startTarget.copy(controls.target);
        cameraMove.endTarget.copy(target);
        cameraMove.startPosition.copy(camera.position);
        cameraMove.endPosition.copy(endPosition);
    };

    const selectProject = (projectRoot, { moveCamera = false } = {}) => {
        if (!projectRoot?.userData?.project) {
            hideProjectSelection();
            return;
        }

        if (selectedProject && selectedProject !== projectRoot) setProjectMeshMaterial(selectedProject, false);
        selectedProject = projectRoot;
        setProjectMeshMaterial(selectedProject, true);
        updateProjectCard(selectedProject);
        autoRotateTarget = 0.42;
        if (moveCamera) animateToProject(projectRoot);
    };

    renderer.domElement.addEventListener('pointermove', (event) => {
        if (!isActive || event.pointerType === 'touch') return;
        const projectRoot = projectAtPointer(event);
        renderer.domElement.style.cursor = projectRoot ? 'pointer' : 'grab';
    });

    renderer.domElement.addEventListener('pointerleave', () => {
        renderer.domElement.style.cursor = isActive ? 'grab' : 'default';
    });

    renderer.domElement.addEventListener('pointerdown', (event) => {
        if (!isActive) return;
        pointerDown = { x: event.clientX, y: event.clientY };
        stopAutomaticOrbit();
    });

    renderer.domElement.addEventListener('pointerup', (event) => {
        if (!isActive || !pointerDown) return;
        const distance = Math.hypot(event.clientX - pointerDown.x, event.clientY - pointerDown.y);
        pointerDown = null;
        if (distance > 8) return;
        const hit = projectAtPointer(event);
        if (hit) {
            selectProject(hit, { moveCamera: true });
        } else {
            hideProjectSelection();
        }
    });

    renderer.domElement.addEventListener('wheel', stopAutomaticOrbit, { passive: true });
    renderer.domElement.addEventListener('touchstart', stopAutomaticOrbit, { passive: true });
    controls.addEventListener('start', stopAutomaticOrbit);


    const activate = () => {
        if (isActive || isTransitioning) return;
        isTransitioning = true;
        isActive = true;
        viewportPortal?.mount();
        controls.enabled = true;
        renderer.domElement.style.cursor = 'grab';
        document.body.classList.add('city-explorer-is-active');
        document.dispatchEvent(new CustomEvent('led:citystate', { detail: { active: true, root } }));
        root.classList.add('is-city-fullscreen', 'is-city-active');
        lenis?.stop?.();

        window.requestAnimationFrame(() => {
            root.classList.add('is-city-fullscreen-visible');
            resize();
            gsap.killTweensOf(root);
            gsap.fromTo(root,
                { autoAlpha: 0 },
                {
                    autoAlpha: 1,
                    duration: 0.72,
                    ease: 'power2.out',
                    overwrite: true,
                    force3D: true,
                    onComplete: () => {
                        isTransitioning = false;
                        root.querySelector('[data-city-close]')?.focus({ preventScroll: true });
                    },
                },
            );
        });
    };

    const deactivate = () => {
        if (!isActive || isTransitioning) return;
        isTransitioning = true;
        isActive = false;
        renderer.domElement.style.cursor = 'default';
        root.classList.remove('is-city-active', 'is-city-fullscreen-visible', 'is-city-project-card-open');
        root.classList.add('is-city-closing');
        hideProjectCard();
        hideProjectSelection();

        clearTimeout(closeTimer);
        const finishClose = () => {
            root.classList.remove('is-city-fullscreen', 'is-city-closing');
            root.style.removeProperty('opacity');
            root.style.removeProperty('visibility');
            viewportPortal?.restore();
            document.body.classList.remove('city-explorer-is-active');
            document.dispatchEvent(new CustomEvent('led:citystate', { detail: { active: false, root } }));
            autoRotateTarget = 0.18;
            controls.enabled = false;
            lenis?.start?.();
            isTransitioning = false;
            resize();
            root.querySelector('[data-city-enter]')?.focus({ preventScroll: true });
            root.dispatchEvent(new CustomEvent('city:closed'));
        };
        gsap.killTweensOf(root);
        gsap.to(root, {
            autoAlpha: 0,
            duration: 0.58,
            ease: 'power2.inOut',
            overwrite: true,
            force3D: true,
            onComplete: finishClose,
        });
    };

    root.querySelector('[data-city-enter]')?.addEventListener('click', activate);
    root.querySelector('[data-city-close]')?.addEventListener('click', deactivate);
    root.addEventListener('city:activate', activate);
    root.addEventListener('city:deactivate', deactivate);

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isActive) {
            if (selectedProject) hideProjectSelection();
            else deactivate();
        }
    });

    const resize = () => {
        const width = Math.max(1, mount.clientWidth);
        const height = Math.max(1, mount.clientHeight);
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);
    resize();

    const updateTheme = (mode) => {
        themeMode = mode === 'night' ? 'night' : 'day';
        palette = CITY_PALETTES[themeMode];
        scene.background.setHex(palette.background);

        hemisphere.color.setHex(palette.ambient);
        hemisphere.groundColor.setHex(palette.ground);
        hemisphere.intensity = themeMode === 'night' ? 1.95 : 2.45;
        sun.color.setHex(palette.sun);
        sun.intensity = themeMode === 'night' ? 2.35 : 3.05;

        groundMaterial.color.setHex(palette.ground);
        blockMaterial.color.setHex(palette.block);
        lawnMaterial.color.setHex(palette.lawn);
        roadMaterial.color.setHex(palette.road);
        roadLineMaterial.color.setHex(palette.roadLine);
        roadLineMaterial.emissive.setHex(palette.roadLine);
        roadLineMaterial.emissiveIntensity = themeMode === 'night' ? 0.22 : 0;
        buildingMaterial.color.setHex(palette.building);
        buildingAltMaterial.color.setHex(palette.buildingAlt);
        buildingDarkMaterial.color.setHex(palette.buildingDark);
        glassMaterial.color.setHex(palette.glass);
        glassMaterial.emissive.setHex(palette.glassEdge);
        glassMaterial.emissiveIntensity = themeMode === 'night' ? 0.18 : 0.03;
        windowMaterial.color.setHex(palette.window);
        windowMaterial.emissive.setHex(palette.window);
        windowMaterial.emissiveIntensity = themeMode === 'night' ? 1.32 : 0;
        windowDarkMaterial.color.setHex(palette.window);
        windowDarkMaterial.emissive.setHex(palette.window);
        windowDarkMaterial.emissiveIntensity = themeMode === 'night' ? 0.025 : 0;
        treeMaterial.color.setHex(palette.tree);
        trunkMaterial.color.setHex(palette.trunk);
        lampPostMaterial.color.setHex(palette.lampPost);
        lampMaterial.color.setHex(palette.lamp);
        lampMaterial.emissive.setHex(palette.lamp);
        lampMaterial.emissiveIntensity = themeMode === 'night' ? 3.2 : 0.18;
        carMaterial.color.setHex(palette.car);
        carAltMaterial.color.setHex(palette.carAlt);
        carAccentMaterial.color.setHex(palette.carAccent);
        parkAccentMaterial.color.setHex(palette.parkAccent);

        projectMaterial.color.setHex(palette.project);
        projectMaterial.emissive.setHex(palette.project);
        projectMaterial.emissiveIntensity = themeMode === 'night' ? 0.2 : 0;
        projectSelectedMaterial.color.setHex(palette.projectSelected);
        projectSelectedMaterial.emissive.setHex(palette.projectSelected);
        projectSelectedMaterial.emissiveIntensity = themeMode === 'night' ? 0.58 : 0.08;
        projectSiteMaterial.color.setHex(palette.projectSite);
        projectSiteMaterial.emissive.setHex(palette.projectSite);
        projectSiteMaterial.emissiveIntensity = themeMode === 'night' ? 0.25 : 0.04;
        projectLoadingMaterial.color.setHex(palette.projectSite);

        nightLightPool.forEach((light) => {
            light.color.setHex(palette.lamp);
            light.intensity = themeMode === 'night' ? 26 : 0;
        });
    };

    window.addEventListener('led:themechange', (event) => updateTheme(event.detail?.mode || 'day'));

    let frame = 0;
    let isVisible = true;
    const visibilityObserver = new IntersectionObserver((entries) => {
        isVisible = entries.some((entry) => entry.isIntersecting);
    }, { rootMargin: '240px 0px', threshold: 0.01 });
    visibilityObserver.observe(root);

    const clock = new THREE.Clock();
    let elapsed = 0;

    const animate = () => {
        frame = requestAnimationFrame(animate);
        const delta = Math.min(clock.getDelta(), 0.05);
        if ((!isVisible && !isActive) || document.hidden) return;

        elapsed += delta;
        autoRotateSpeed = damp(autoRotateSpeed, autoRotateTarget, 2.8, delta);
        controls.autoRotate = autoRotateSpeed > 0.002;
        controls.autoRotateSpeed = autoRotateSpeed;

        if (cameraMove.active) {
            cameraMove.elapsed += delta;
            const progress = THREE.MathUtils.clamp(cameraMove.elapsed / cameraMove.duration, 0, 1);
            const eased = easeInOut(progress);
            controls.target.lerpVectors(cameraMove.startTarget, cameraMove.endTarget, eased);
            camera.position.lerpVectors(cameraMove.startPosition, cameraMove.endPosition, eased);
            if (progress >= 1) cameraMove.active = false;
        }

        controls.update(delta);
        refreshTiles();
        updateTraffic(elapsed);
        renderer.render(scene, camera);
    };
    animate();

    return {
        activate,
        deactivate,
        resize,
        destroy() {
            clearTimeout(closeTimer);
            viewportPortal?.restore();
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            visibilityObserver.disconnect();
            controls.dispose();
            renderer.dispose();
            disposableMaterials.forEach((material) => material.dispose());
            activeTiles.forEach((tile) => {
                tile.traverse((child) => child.geometry?.dispose?.());
            });
            projectLayer.traverse((child) => child.geometry?.dispose?.());
            trafficLayer.traverse((child) => child.geometry?.dispose?.());
            nightLightPool.forEach((light) => scene.remove(light));
            mount.replaceChildren();
        },
    };
}

export function initCityExplorers({ lenis = null } = {}) {
    return [...document.querySelectorAll('[data-city-explorer]')]
        .map((root) => initCityExplorer(root, { lenis }))
        .filter(Boolean);
}
